import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import { CollapsibleV2 } from '~/components/CollapsibleV2';
import { Variant } from '~/components/isolated/IconButton';
import { Switch } from '~/components/Switch';
import { catalogStore } from '~/stores';
import {
  ProductFilters,
  ProductPropertyFilterCode,
} from '~/stores/CatalogStore';
import { ProductPropertiesFilter } from '~/types/Product/interface';

import RangeInput from '../RangeInput/RangeInput';

import ProductMultiselectFilter from './ProductMultiselectFilter';
import ProductTypeListFilter from './ProductRadioListFilter';

interface ProductFilterProps {
  filter: ProductPropertiesFilter;
  selectedFilters: ProductFilters;
  collapsible?: boolean;
  onChange?: (code: ProductPropertyFilterCode, values: any | any[]) => void;
  noCollapsible?: boolean;
  isCompact?: boolean;
  mobile?: boolean;
  classes?: {
    root?: string;
    inner?: string;
    bodyContainer?: string;
  };
}

const ProductFilter = ({
  filter,
  onChange,
  selectedFilters,
  noCollapsible,
  mobile,
  classes,
}: ProductFilterProps) => {
  const {
    root: className,
    inner: innerClassName,
    bodyContainer: bodyContainerClassName,
  } = classes || {};
  const { t } = useTranslation();

  const label = filter.name ?? t(`properties:${filter.code}`);
  const onValueChange = (value: any) => onChange?.(filter.code, value);

  const collapseExpandBtnVariant: Variant = mobile ? 'outline' : 'transparent';

  if (filter.code === 'express_delivery') {
    return (
      <Switch
        label={label}
        labelPosition="left"
        value={selectedFilters.properties?.[filter.code]}
        onChange={onValueChange}
        className={className}
      />
    );
  }

  if (filter.code === 'price') {
    return (
      <CollapsibleV2
        isCollapsed={!filter.showExpanded}
        title={`${company.config.currency} ${t('price')} `}
        className={className}
        expandIconButtonVariant={collapseExpandBtnVariant}
        innerClassName={innerClassName}
        bodyContainerClassName={bodyContainerClassName}
      >
        <RangeInput
          onChange={({ min, max }) => onValueChange({ from: min, to: max })}
          step={1}
          min={catalogStore.pricesminmax.priceMinPounds}
          max={catalogStore.pricesminmax.priceMaxPounds}
          displayRangeSlider={false}
          currentMin={selectedFilters.price?.from}
          currentMax={selectedFilters.price?.to}
        />
      </CollapsibleV2>
    );
  }

  if (filter.code === 'type') {
    if (filter.values.length < 2) {
      return null;
    }

    return (
      <ProductTypeListFilter
        isCollapsed={!filter.showExpanded}
        label={label}
        values={filter.values}
        currentValues={selectedFilters.properties?.[filter.code]}
        onChange={onValueChange}
        className={className}
        noCollapsible={noCollapsible}
        innerClassName={innerClassName}
        bodyContainerClassName={bodyContainerClassName}
      />
    );
  }

  if (filter.type === 'toggle') {
    return (
      <Switch
        label={label}
        labelPosition="left"
        value={selectedFilters.properties?.[filter.code]}
        onChange={onValueChange}
        className={className}
      />
    );
  }

  if (
    filter.type === 'selector' ||
    filter.type === 'colorList' ||
    filter.type === 'sizeList'
  ) {
    return (
      <ProductMultiselectFilter
        isCollapsed={!filter.showExpanded}
        label={label}
        values={filter.values}
        currentValues={selectedFilters.properties?.[filter.code]}
        onChange={onValueChange}
        className={className}
        type={filter.type}
        expandIconButtonVariant={collapseExpandBtnVariant}
        innerClassName={innerClassName}
        bodyContainerClassName={bodyContainerClassName}
      />
    );
  }

  return (
    <CollapsibleV2
      title={label}
      className={className}
      expandIconButtonVariant={collapseExpandBtnVariant}
      innerClassName={innerClassName}
      bodyContainerClassName={bodyContainerClassName}
    />
  );
};

export default observer(ProductFilter);
